import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const SOCIAL_PLATFORMS = [
  { id: 'instagram', label: 'Instagram', icon: 'Instagram', placeholder: '@yourhandle', color: '#E1306C' },
  { id: 'tiktok', label: 'TikTok', icon: 'Music', placeholder: '@yourhandle', color: '#69C9D0' },
  { id: 'youtube', label: 'YouTube', icon: 'Youtube', placeholder: 'channel name', color: '#FF0000' },
  { id: 'twitter', label: 'Twitter/X', icon: 'Twitter', placeholder: '@yourhandle', color: '#1DA1F2' },
];

const FOLLOWER_RANGES = [
  { value: '1k-10k', label: '1K – 10K' },
  { value: '10k-50k', label: '10K – 50K' },
  { value: '50k-100k', label: '50K – 100K' },
  { value: '100k-500k', label: '100K – 500K' },
  { value: '500k-1m', label: '500K – 1M' },
  { value: '1m+', label: '1M+' },
];

const CONTENT_CATEGORIES = [
  { value: 'lifestyle', label: 'Lifestyle', icon: 'Home' },
  { value: 'fashion', label: 'Fashion', icon: 'Shirt' },
  { value: 'tech', label: 'Tech', icon: 'Smartphone' },
  { value: 'food', label: 'Food', icon: 'UtensilsCrossed' },
  { value: 'travel', label: 'Travel', icon: 'Plane' },
  { value: 'fitness', label: 'Fitness', icon: 'Dumbbell' },
  { value: 'beauty', label: 'Beauty', icon: 'Sparkles' },
  { value: 'gaming', label: 'Gaming', icon: 'Gamepad2' },
  { value: 'finance', label: 'Finance', icon: 'TrendingUp' },
  { value: 'education', label: 'Education', icon: 'BookOpen' },
];

const InfluencerVerificationForm = ({ formData, onChange, errors }) => {
  const [verifyingHandle, setVerifyingHandle] = useState({});
  const [verifiedHandles, setVerifiedHandles] = useState({});

  const handleSocialHandleChange = (platform, value) => {
    onChange('socialHandles', { ...formData?.socialHandles, [platform]: value });
    if (verifiedHandles?.[platform]) {
      setVerifiedHandles(prev => { const n = { ...prev }; delete n?.[platform]; return n; });
    }
  };

  const handleVerifyHandle = async (platform) => {
    const handle = formData?.socialHandles?.[platform];
    if (!handle?.trim()) return;
    setVerifyingHandle(prev => ({ ...prev, [platform]: true }));
    await new Promise(r => setTimeout(r, 1200));
    setVerifyingHandle(prev => ({ ...prev, [platform]: false }));
    setVerifiedHandles(prev => ({ ...prev, [platform]: true }));
  };

  const handleFollowerRangeChange = (platform, value) => {
    onChange('followerRanges', { ...formData?.followerRanges, [platform]: value });
  };

  const handleCategoryToggle = (value) => {
    const current = formData?.categories || [];
    const updated = current?.includes(value)
      ? current?.filter(c => c !== value)
      : [...current, value];
    onChange('categories', updated);
  };

  const filledPlatforms = SOCIAL_PLATFORMS?.filter(p => formData?.socialHandles?.[p?.id]?.trim());

  return (
    <div className="space-y-8">
      {/* Social Handles */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Icon name="AtSign" size={18} color="var(--color-primary)" />
          <h3 className="text-base font-semibold text-[var(--color-foreground)]">Social Media Handles</h3>
          <span className="text-xs text-[var(--color-error)] font-medium">*Required (at least one)</span>
        </div>
        {errors?.socialHandles && (
          <p className="text-xs text-[var(--color-error)] mb-3">{errors?.socialHandles}</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SOCIAL_PLATFORMS?.map(platform => (
            <div key={platform?.id} className="bg-[var(--color-muted)]/30 rounded-xl p-4 border border-[var(--color-border)]">
              <div className="flex items-center gap-2 mb-3">
                <Icon name={platform?.icon} size={16} color={platform?.color} />
                <span className="text-sm font-medium text-[var(--color-foreground)]">{platform?.label}</span>
                {verifiedHandles?.[platform?.id] && (
                  <span className="ml-auto flex items-center gap-1 text-[var(--color-success)] text-xs">
                    <Icon name="CheckCircle" size={12} />
                    Verified
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder={platform?.placeholder}
                  value={formData?.socialHandles?.[platform?.id] || ''}
                  onChange={e => handleSocialHandleChange(platform?.id, e?.target?.value)}
                  className="flex-1 h-9 px-3 rounded-lg bg-[var(--color-background)] border border-[var(--color-border)] text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/50"
                />
                <button
                  type="button"
                  onClick={() => handleVerifyHandle(platform?.id)}
                  disabled={!formData?.socialHandles?.[platform?.id]?.trim() || verifyingHandle?.[platform?.id]}
                  className="px-3 h-9 rounded-lg bg-[var(--color-primary)]/20 text-[var(--color-primary)] text-xs font-medium hover:bg-[var(--color-primary)]/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {verifyingHandle?.[platform?.id] ? (
                    <Icon name="Loader" size={12} className="animate-spin" />
                  ) : 'Verify'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Follower Ranges - only for filled platforms */}
      {filledPlatforms?.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Icon name="Users" size={18} color="var(--color-secondary)" />
            <h3 className="text-base font-semibold text-[var(--color-foreground)]">Follower Count</h3>
            <span className="text-xs text-[var(--color-error)] font-medium">*Required</span>
          </div>
          <div className="space-y-4">
            {filledPlatforms?.map(platform => (
              <div key={platform?.id}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon name={platform?.icon} size={14} color={platform?.color} />
                  <span className="text-sm text-[var(--color-muted-foreground)]">{platform?.label} followers</span>
                </div>
                {errors?.[`followerRanges.${platform?.id}`] && (
                  <p className="text-xs text-[var(--color-error)] mb-2">{errors?.[`followerRanges.${platform?.id}`]}</p>
                )}
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  {FOLLOWER_RANGES?.map(range => (
                    <button
                      key={range?.value}
                      type="button"
                      onClick={() => handleFollowerRangeChange(platform?.id, range?.value)}
                      className={`py-2 px-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                        formData?.followerRanges?.[platform?.id] === range?.value
                          ? 'bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] text-white shadow-md'
                          : 'bg-[var(--color-muted)]/40 text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)] border border-[var(--color-border)]'
                      }`}
                    >
                      {range?.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content Categories */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Tag" size={18} color="var(--color-accent)" />
          <h3 className="text-base font-semibold text-[var(--color-foreground)]">Content Categories</h3>
          <span className="text-xs text-[var(--color-error)] font-medium">*Required (at least one)</span>
        </div>
        {errors?.categories && (
          <p className="text-xs text-[var(--color-error)] mb-3">{errors?.categories}</p>
        )}
        <div className="flex flex-wrap gap-3">
          {CONTENT_CATEGORIES?.map(cat => (
            <button
              key={cat?.value}
              type="button"
              onClick={() => handleCategoryToggle(cat?.value)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                formData?.categories?.includes(cat?.value)
                  ? 'bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] text-white shadow-md scale-105'
                  : 'bg-[var(--color-card)] text-[var(--color-foreground)] hover:bg-[var(--color-muted)] border border-[var(--color-border)]'
              }`}
            >
              <Icon
                name={cat?.icon}
                size={14}
                color={formData?.categories?.includes(cat?.value) ? '#FFFFFF' : 'var(--color-foreground)'}
              />
              {cat?.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InfluencerVerificationForm;
